import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { EarningsDate } from '../api/models/EarningsDate';
import { getEarningsDates, getEarningsReport } from '../api/thunks/financials';
import { setNotification } from '../store/notifications-reducer';
import { useLocation } from 'react-router-dom';
import { setSelectedEarningsReport } from '../store/layout-reducer';
import { EarningsReportV2 } from '../api/models/EarningsReportV2';

export const useEarningsReport = (
  supportedTickers: string[],
  accessToken: string | undefined,
  isShareMode: boolean,
  conversationId: string | undefined,
  isTableView?: boolean
) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const shareLinkToken = new URLSearchParams(location.search).get('share') ?? undefined;
  const conversationLayout = useAppSelector(state => conversationId ? state.layout[conversationId] : undefined);

  const [currentTicker, setCurrentTickerState] = useState('');
  const [fiscalPeriod, setFiscalPeriod] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');

  const [reports, setReports] = useState<EarningsReportV2[]>([]);
  const [dates, setDates] = useState<EarningsDate[]>([]);

  useEffect(() => {
    if (conversationLayout) {
        setCurrentTickerState(conversationLayout.selectedEarningsReport?.ticker || supportedTickers[0] || '');
        setFiscalPeriod(conversationLayout.selectedEarningsReport?.fiscalPeriod || '');
        setFiscalYear(conversationLayout.selectedEarningsReport?.fiscalYear || '');
    } else if (supportedTickers.length > 0 && !currentTicker) {
        // Set default ticker if no layout is available yet
        setCurrentTickerState(supportedTickers[0]);
    }
  }, [conversationId, conversationLayout, supportedTickers, currentTicker]);

  const setCurrentTicker = (ticker: string) => {
    setCurrentTickerState(ticker);
    setFiscalPeriod(''); // Reset fiscal period when ticker changes
    setFiscalYear(''); // Reset fiscal year when ticker changes
    if (conversationId) {
      dispatch(setSelectedEarningsReport({
        conversationId,
        report: {
          ticker,
          fiscalPeriod: '',
          fiscalYear: ''
        }
      }));
    }
  };

  const setCurrentFiscalPeriodAndYear = (period: string, year: string) => {
    setFiscalPeriod(period);
    setFiscalYear(year);
    
    if (conversationId && currentTicker) {
      dispatch(setSelectedEarningsReport({
        conversationId,
        report: {
          ticker: currentTicker,
          fiscalPeriod: period,
          fiscalYear: year
        }
      }));
    }
  };

  useEffect(() => {
    if (supportedTickers.length > 0 && !supportedTickers.includes(currentTicker)) {
      setCurrentTicker(supportedTickers[0]);
    }
  }, [supportedTickers, currentTicker]);

  // Ensure fiscalDateEnding is synced when fiscalPeriod/fiscalYear or dates change
  useEffect(() => {
    if (fiscalPeriod && fiscalYear && dates.length > 0) {
      const correspondingDate = dates.find(d => d.fiscalPeriod === fiscalPeriod && d.fiscalYear === fiscalYear);
      if (correspondingDate && (correspondingDate.fiscalPeriod !== fiscalPeriod || correspondingDate.fiscalYear !== fiscalYear)) {
        setFiscalPeriod(correspondingDate.fiscalPeriod!);
        setFiscalYear(correspondingDate.fiscalYear!)
      }
    }
  }, [fiscalPeriod, fiscalYear, dates]);

  useEffect(() => {
    if (!currentTicker || (!accessToken && !isShareMode)) return;

    const loadEarningsDates = async () => {
      try {
        const data = await getEarningsDates(
          { ticker: currentTicker, entityType: 'earnings' },
          { accessToken, shareLinkToken }
        );

        setDates(data.dates);

        if (data.dates.length > 0) {
          if (!fiscalPeriod || !fiscalYear) {
            const mostRecentWithAccess = data.dates.find(date => date.userHasAccess);
            if (mostRecentWithAccess) {
              setCurrentFiscalPeriodAndYear(mostRecentWithAccess.fiscalPeriod || '', mostRecentWithAccess.fiscalYear || '');
            }
          }
        } else {
          setCurrentFiscalPeriodAndYear('', '');
        }
      } catch (error) {
        console.error(error);
        dispatch(setNotification({
          message: 'Failed to get earnings dates',
          type: 'error'
        }));
      }
    };

    loadEarningsDates();
  }, [currentTicker, accessToken, isShareMode, shareLinkToken]);

  useEffect(() => {
    if (!currentTicker || !fiscalPeriod || !fiscalYear || dates.length === 0 || (!accessToken && !isShareMode)) return;

    const loadEarningsReports = async () => {
      try {
        const selectedDate = dates.find(d => d.fiscalPeriod === fiscalPeriod && d.fiscalYear === fiscalYear);
        if (!selectedDate) {
          setReports([]);
          return;
        }

        const dateIndex = dates.findIndex(d => d.fiscalPeriod === fiscalPeriod && d.fiscalYear === fiscalYear);
        
        let accessibleDatesInRange;
        if (isTableView) {
          // In table view, load all accessible reports starting from the most recent
          accessibleDatesInRange = dates
            .filter(d => d.userHasAccess)
            .slice(0, 5); // Show 5 reports for better readability
        } else {
          // In compact view, load 5 reports starting from the selected period
          accessibleDatesInRange = dates
            .slice(dateIndex)
            .filter(d => d.userHasAccess)
            .slice(0, 5);
        }

        const reportPromises = accessibleDatesInRange.map(date =>
          getEarningsReport(
            currentTicker,
            date.fiscalPeriod || '',
            date.fiscalYear || '',
            { accessToken, shareLinkToken },
            dispatch
          )
        );

        const resolvedReports = await Promise.all(reportPromises);
        setReports(resolvedReports.filter(r => r) as EarningsReportV2[]);
      } catch (error) {
        console.error("error getting earnings reports", error)
      }
    };

    loadEarningsReports();
  }, [currentTicker, fiscalPeriod, fiscalYear, dates, accessToken, isShareMode, shareLinkToken, isTableView]);

  const loadNextReport = async () => {
    if (!currentTicker || reports.length === 0 || dates.length === 0 || (!accessToken && !isShareMode)) return;

    const lastReport = reports[reports.length - 1];
    const lastDateIndex = dates.findIndex(d => d.fiscalPeriod === lastReport.fiscalPeriod && d.fiscalYear === lastReport.fiscalYear);

    if (lastDateIndex !== -1 && lastDateIndex + 1 < dates.length) {
      const nextDate = dates[lastDateIndex + 1];
      if (nextDate && nextDate.userHasAccess) {
        try {
          const nextReport = await getEarningsReport(
            currentTicker,
            nextDate.fiscalPeriod || '',
            nextDate.fiscalYear || '',
            { accessToken, shareLinkToken },
            dispatch
          );
          if (nextReport) {
            setReports(prevReports => [...prevReports, nextReport]);
          }
        } catch (error) {
          console.error("error getting next earnings report", error)
        }
      }
    }
  };

  return {
    currentTicker,
    setCurrentTicker,
    reports,
    fiscalPeriod,
    fiscalYear,
    setFiscalPeriod,
    setFiscalYear,
    setCurrentFiscalPeriodAndYear,
    dates,
    loadNextReport,
  };
};